import { forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { SectionHead } from "@/components/site/Reveal";
import { CATEGORIES, BRANDS, waLink } from "@/data/site";

const BrandChip = ({ b, i }) => (
  <motion.div layout initial={{ opacity: 0, y: 16, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
    className="rounded-2xl bg-white h-20 grid place-items-center relative px-3" data-testid={`explorer-brand-${b.name.toLowerCase().replace(/[^a-z]/g, "")}`}>
    {b.logo
      ? <img src={b.logo} alt={`${b.name} logo`} className={`max-h-9 max-w-[85%] object-contain ${b.invert ? "invert" : ""}`} />
      : <span className="font-display font-bold text-[#030712] text-center text-sm">{b.name}</span>}
    {b.primary && <span className="absolute -top-2 -right-2 h-4 w-4 rounded-full bg-[#39FF14] shadow-[0_0_12px_#39FF14]" title="Primary brand" />}
  </motion.div>
);

const Panel = ({ cat }) => {
  const brands = cat.brands.map((n) => BRANDS.find((b) => b.name === n)).filter(Boolean);
  return (
    <motion.div key={cat.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="grid lg:grid-cols-12 gap-8 glass rounded-[32px] p-4 md:p-6" data-testid="explorer-panel">
      <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto lg:min-h-[380px] rounded-3xl overflow-hidden clip-notch">
        <motion.img key={cat.img} src={cat.img} alt={cat.alt} initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.2 }} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent" />
        <span className="absolute left-5 bottom-5 font-mono text-[11px] tracking-[0.2em] uppercase rounded-full px-3 py-1.5 bg-[#030712]/80 border border-[#39FF14]/30 text-[#39FF14]">{cat.tag}</span>
      </div>
      <div className="lg:col-span-7 flex flex-col justify-between gap-8 p-2 md:p-4">
        <div>
          <h3 className="font-display text-3xl md:text-4xl font-bold" data-testid="explorer-title">{cat.title}</h3>
          <p className="mt-4 text-[#a5b4c8] leading-relaxed max-w-xl">{cat.text}</p>
        </div>
        <div>
          <p className="eyebrow mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">{brands.length} brands & ranges in stock
            {brands.some((b) => b.primary) && <span className="flex items-center gap-2 text-[#a5b4c8]"><span className="h-2.5 w-2.5 rounded-full bg-[#39FF14]" /> Primary brand</span>}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {brands.map((b, i) => <BrandChip key={b.name} b={b} i={i} />)}
          </div>
        </div>
        <a href={waLink(`Hi Rama Electricals, I'd like a price for ${cat.title}. Brands I'm interested in: `)} target="_blank" rel="noreferrer"
          className="btn-neon self-start" data-testid="explorer-whatsapp-btn"><MessageCircle size={18} /> Ask for {cat.title} prices</a>
      </div>
    </motion.div>
  );
};

export const CategoryExplorer = forwardRef(({ active, onSelect }, ref) => {
  const cat = CATEGORIES.find((c) => c.id === active) ?? CATEGORIES[0];
  return (
    <section ref={ref} className="px-6 py-24 scroll-mt-24" data-testid="category-explorer">
      <div className="mx-auto max-w-7xl">
        <SectionHead eyebrow="Explore by category" title={["Pick a category.", "See every brand we stock."]} />
        <div className="mt-10 flex gap-2 overflow-x-auto pb-2 -mx-6 px-6 md:mx-0 md:px-0 md:flex-wrap" role="tablist">
          {CATEGORIES.map((c) => (
            <button key={c.id} role="tab" aria-selected={c.id === cat.id} onClick={() => onSelect(c.id)} data-testid={`explorer-tab-${c.id}`}
              className={`relative shrink-0 rounded-full px-5 py-3 text-sm transition-colors duration-300 ${c.id === cat.id ? "text-[#030712]" : "text-slate-300 hover:text-white border border-white/15"}`}>
              {c.id === cat.id && <motion.span layoutId="explorer-pill" className="absolute inset-0 rounded-full bg-[#39FF14] shadow-[0_0_24px_rgba(57,255,20,0.45)]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
              <span className="relative font-medium">{c.title}</span>
            </button>
          ))}
        </div>
        <div className="mt-8">
          <AnimatePresence initial={false}><Panel key={cat.id} cat={cat} /></AnimatePresence>
        </div>
      </div>
    </section>
  );
});
